import React, { useCallback, useEffect, useState, lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface SlideData {
  title: string;
  image: string;
}
const slides: SlideData[] = [{
  title: 'Biblia infantil — Amor y Cuidado de Dios',
  image: 'https://cdn.magicpatterns.com/uploads/wyD6wq929fmfboEzNfrMnW/Tomo-1-Amor-y-Cuidado-de-Dios.png'
}, {
  title: 'Parábola — El osito que agradeció antes de comer',
  image: 'https://cdn.magicpatterns.com/uploads/nwab9GEhqjAJjwfPKRmzHE/Tomo-9-Oracion-y-Amistad-con-Dios_(1).png'
}, {
  title: 'Devocional — Aprendo a obedecer',
  image: 'https://cdn.magicpatterns.com/uploads/sMWXKsNdqErnbSPpdxG7k9/1_Aprendo-a-obedecer.png'
}, {
  title: 'Devocional — Ser amable con otros',
  image: 'https://cdn.magicpatterns.com/uploads/jwneRFhCMidqzmRRtrTd3Q/2_Ser-amable-con-otros.png'
}, {
  title: 'Cuaderno de oración — Amistad con Dios',
  image: 'https://cdn.magicpatterns.com/uploads/uKNJBsdwTCoYyox15mGLL5/Tomo-9-Oracion-y-Amistad-con-Dios.png'
}, {
  title: 'Cuaderno de oración — Lo que siento en mi corazón',
  image: 'https://cdn.magicpatterns.com/uploads/9sKJdY1RG5PQ4JSfCQ4Gqk/1_Lo-que-siento-en-mi-corazon.png'
}, {
  title: 'Cuaderno de oración — Un dibujo para Dios',
  image: 'https://cdn.magicpatterns.com/uploads/uyo7YkMe6a7u6Fn3ZrduGU/2_Un-dibujo-para-Dios.png'
}, {
  title: 'Juego imprimible — Encuentra las 5 diferencias',
  image: 'https://cdn.magicpatterns.com/uploads/eM5TRgBaCazMFMDmTigSZR/e981268c-7301-4d9e-b707-761362132225.jpg'
}, {
  title: 'Biblia para colorear — El arca de Noé',
  image: 'https://cdn.magicpatterns.com/uploads/jpgZhHu7NA8wQ7joSeGtZn/22989a99-6a11-4485-9f9f-8e46f93647dd.jpg'
}];
function ArrowButton({
  direction,
  onClick,
  disabled
}: {
  direction: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
}) {
  return <button onClick={onClick} disabled={disabled} aria-label={direction === 'left' ? 'Anterior' : 'Siguiente'} className={`
        w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center
        transition-all duration-200 shadow-sm
        ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
      `} style={{
    backgroundColor: 'white',
    border: '1px solid var(--color-border)'
  }} onMouseEnter={e => {
    if (disabled) return;
    e.currentTarget.style.backgroundColor = '#FEF6E8';
    e.currentTarget.style.borderColor = 'var(--color-gold)';
    const icon = e.currentTarget.querySelector('svg');
    if (icon) (icon as HTMLElement).style.color = 'var(--color-gold)';
  }} onMouseLeave={e => {
    e.currentTarget.style.backgroundColor = 'white';
    e.currentTarget.style.borderColor = 'var(--color-border)';
    const icon = e.currentTarget.querySelector('svg');
    if (icon) (icon as HTMLElement).style.color = 'var(--color-deep-blue)';
  }} data-id="element-148">
      {direction === 'left' ? <ChevronLeftIcon className="w-5 h-5" style={{
      color: 'var(--color-deep-blue)'
    }} data-id="element-149" /> : <ChevronRightIcon className="w-5 h-5" style={{
      color: 'var(--color-deep-blue)'
    }} data-id="element-150" />}
    </button>;
}
export function PreviewCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const goNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, slides.length - 1));
  }, []);
  const goPrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < slides.length - 1;
  // Card width and gap for the sliding calculation
  const cardWidth = isMobile ? 280 : 340;
  const gap = isMobile ? 16 : 24;
  // Calculate translateX to center the current card
  // We want the current card centered in the viewport area
  const translateX = -(currentIndex * (cardWidth + gap));
  return <section className="py-10 md:py-14 relative overflow-hidden" style={{
    backgroundColor: 'var(--color-sky-light)'
  }} data-id="element-151">
      <div className="container mx-auto px-4" data-id="element-152">
        {/* Header */}
        <FadeIn data-id="element-153">
          <div className="max-w-6xl mx-auto mb-8 md:mb-10" data-id="element-154">
            <h2 className="text-3xl md:text-4xl font-bold mb-2" style={{
            color: 'var(--color-deep-blue)'
          }} data-id="element-155">
              Así se ve por dentro
            </h2>
            <div className="w-12 h-1.5 rounded-full mb-3" style={{
            backgroundColor: 'var(--color-gold)'
          }} data-id="element-156" />
            <p className="text-base md:text-lg" style={{
            color: 'var(--color-body)'
          }} data-id="element-157">
              Materiales visuales, prácticos y amorosos listos para usar.
            </p>
          </div>
        </FadeIn>

        {/* Carousel container */}
        <div className="relative max-w-6xl mx-auto" data-id="element-158">
          {/* Gradient overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-8 md:w-20 z-20 pointer-events-none" style={{
          background: 'linear-gradient(to right, var(--color-sky-light), transparent)'
        }} data-id="element-159" />
          <div className="absolute right-0 top-0 bottom-0 w-8 md:w-20 z-20 pointer-events-none" style={{
          background: 'linear-gradient(to left, var(--color-sky-light), transparent)'
        }} data-id="element-160" />

          {/* Arrow buttons - absolutely positioned */}
          <div className="absolute left-0 md:left-2 top-1/2 -translate-y-1/2 z-30 -ml-1 md:-ml-3" data-id="element-161">
            <ArrowButton direction="left" onClick={goPrev} disabled={!canGoPrev} data-id="element-162" />
          </div>
          <div className="absolute right-0 md:right-2 top-1/2 -translate-y-1/2 z-30 -mr-1 md:-mr-3" data-id="element-163">
            <ArrowButton direction="right" onClick={goNext} disabled={!canGoNext} data-id="element-164" />
          </div>

          {/* Slides track */}
          <div className="overflow-hidden mx-8 md:mx-14" data-id="element-165">
            <div className="flex transition-transform duration-500 ease-out" style={{
            transform: `translateX(${translateX}px)`,
            gap: `${gap}px`
          }} data-id="element-166">
              {slides.map((slide, index) => {
              const isActive = index === currentIndex;
              return <div key={index} className="flex-shrink-0 transition-all duration-500" style={{
                width: `${cardWidth}px`
              }} data-id="element-167">
                    <div className={`
                        rounded-2xl overflow-hidden bg-white
                        transition-all duration-500 ease-out
                        ${isActive ? 'shadow-xl' : 'shadow-md opacity-60 scale-[0.94]'}
                      `} style={{
                  border: isActive ? '2px solid var(--color-gold)' : '1px solid var(--color-border)'
                }} data-id="element-168">
                      <div className="w-full aspect-[3/4] overflow-hidden bg-gray-50" data-id="element-169">
                        <img src={slide.image} alt={slide.title} className="w-full h-full object-cover object-top" loading="lazy" data-id="element-170" />
                      </div>
                      <div className="px-5 py-4" data-id="element-171">
                        <h3 className="text-sm md:text-base font-bold leading-snug" style={{
                      color: 'var(--color-deep-blue)'
                    }} data-id="element-172">
                          {slide.title}
                        </h3>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Dots indicator */}
        <div className="flex items-center justify-center gap-2 mt-6 md:mt-8" data-id="element-173">
          {slides.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} aria-label={`Ir a slide ${index + 1}`} className="transition-all duration-300 rounded-full" style={{
          width: index === currentIndex ? '24px' : '8px',
          height: '8px',
          backgroundColor: index === currentIndex ? 'var(--color-gold)' : 'var(--color-border)'
        }} data-id="element-174" />)}
        </div>
      </div>
    </section>;
}